import {
  Location,
  TideHeightPrediction,
  TideHeightPredictionRequest,
  // MonthlyServiceOpportunitiesRequest, // Replaced by SearchParameters
  SearchParameters, // New composite request object
  SearchParametersDTO, // DTO version for API transmission
  MonthlySchedule,
  DailySchedule
} from '../models';
import { formatLocalDateAsISOString } from '../utils/dateUtils';

// API base URL - should be configured for your environment
const isDev = process.env.NODE_ENV === 'development';
// Base URL already includes /api for our new endpoint structure
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (isDev ? 'http://localhost:5000' : '');

/**
 * API client for interacting with the Tide Analyzer Web API
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    console.log('API Client initialized with base URL:', this.baseUrl);
  }

  /**
   * Performs a GET request to the API
   */
  private async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    try {
      // Handle relative URLs by adding the window's origin
      let urlString: string;
      if (this.baseUrl.startsWith('http')) {
        // Absolute URL with protocol
        urlString = `${this.baseUrl}/${endpoint}`;
      } else {
        // Relative URL, use current window location
        urlString = `${window.location.origin}${this.baseUrl}/${endpoint}`;
      }
      
      const url = new URL(urlString);
      
      // Add query parameters if provided
      if (params) {
        Object.keys(params).forEach(key => {
          url.searchParams.append(key, params[key]);
        });
      }
      
      console.log(`Making GET request to: ${url.toString()}`);
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      return await response.json() as T;
    } catch (error) {
      console.error(`Error in GET request to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Performs a POST request to the API
   */
  private async post<T>(endpoint: string, data: any): Promise<T> {
    try {
      // Handle relative URLs by adding the window's origin
      let urlString: string;
      if (this.baseUrl.startsWith('http')) {
        // Absolute URL with protocol
        urlString = `${this.baseUrl}/${endpoint}`;
      } else {
        // Relative URL, use current window location
        urlString = `${window.location.origin}${this.baseUrl}/${endpoint}`;
      }
      
      console.log(`Making POST request to: ${urlString} with data:`, data);
      
      const response = await fetch(urlString, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      return await response.json() as T;
    } catch (error) {
      console.error(`Error in POST request to ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Searches for locations matching a search term
   */
  async searchLocations(searchTerm: string, limit: number = 10): Promise<Location[]> {
    return this.get<Location[]>('api/tide/locations/search', { term: searchTerm, limit });
  }

  /**
   * Gets a location by ID
   */
  async getLocationById(id: number): Promise<Location> {
    return this.get<Location>(`api/tide/locations/${id}`);
  }

  /**
   * Gets monthly service opportunities
   * This is the consolidated service scheduling endpoint
   */
  async getMonthlyServiceOpportunities(request: SearchParameters | SearchParametersDTO): Promise<MonthlySchedule> {
    // Check if we need to convert from SearchParameters to SearchParametersDTO
    let dto: SearchParametersDTO;
    
    if ('location' in request) {
      // It's a SearchParameters object, convert to DTO
      dto = {
        locationId: request.location.id,
        dateOfInterest: request.dateOfInterest,
        services: request.services
      };
    } else {
      // It's already a DTO
      dto = request;
    }
    
    // Log the original date object before serialization
    console.log('Original dateOfInterest before API call:', dto.dateOfInterest);
    console.log('dateOfInterest toString():', dto.dateOfInterest.toString());
    
    // WARNING: Never use toISOString().split('T')[0] for formatting dates as it causes timezone issues
    // Always use formatLocalDateAsISOString() from dateUtils instead
    
    // Use timezone-safe date formatting that preserves the local date regardless of timezone
    const datePartOnly = formatLocalDateAsISOString(dto.dateOfInterest);
    console.log('Date part extracted for API (timezone-safe):', datePartOnly);

    // Create a serializable version of the DTO with dateOfInterest converted to ISO date string
    // DateOnly in C# needs just the date portion (YYYY-MM-DD) without time
    const serializedRequest = {
      ...dto,
      dateOfInterest: datePartOnly // Just get YYYY-MM-DD part
    };
    
    console.log('Final serialized request:', serializedRequest);
    
    return this.post<MonthlySchedule>('api/tide/predictions/opportunities', serializedRequest);
  }
  
  /**
   * Tests the backend connectivity
   */
  async testBackend(): Promise<string> {
    try {
      return this.get<string>('api/tide/health');
    } catch (error) {
      console.error('Backend test failed:', error);
      throw error;
    }
  }
}

// Create a singleton instance of the API client
export const apiClient = new ApiClient();
